import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Tag,
  Space,
  Button,
  Modal,
  message,
  Tooltip,
  Card,
  Statistic,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { RollbackOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getCreditAllocationHistory,
  rollbackCreditAllocation,
} from "../../../api/accounting";
import { formatCurrencyWithConversion, formatInDisplayCurrency } from "../../../utils/currencyUtils";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";

const CreditAllocationHistory = ({ selectedBrand }) => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey } = useDisplayCurrency();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [dateFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const gridApiRef = useRef(null);

  // Cache key for persistence
  const cacheKey = selectedBrand
    ? `credit-allocation-history-cache-${selectedBrand.id}`
    : null;

  // Load from cache on mount - use ref to avoid re-renders
  const cachedDataRef = useRef(null);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState(false);
  const fetchingRef = useRef(false); // Prevent multiple simultaneous requests
  const lastFetchKeyRef = useRef(null); // Track last fetch to prevent duplicates
  const fetchHistoryRef = useRef(null); // Store latest fetch function

  // Initialize cache on mount and when page changes
  useEffect(() => {
    if (!cacheKey) {
      cachedDataRef.current = null;
      setHasLoadedFromCache(false);
      return;
    }
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        if (cacheAge < 5 * 60 * 1000) {
          cachedDataRef.current = parsed.data;
          // Load current page from cache immediately if available
          const cacheKeyForPage = `page-${currentPage}-size-${pageSize}`;
          if (parsed.data && parsed.data[cacheKeyForPage]) {
            const cached = parsed.data[cacheKeyForPage];
            setHistoryData(cached.results || []);
            setTotalCount(cached.count || 0);
            setHasLoadedFromCache(true);
            setLoading(false);
            return;
          }
        } else {
          // Cache expired, clear it
          cachedDataRef.current = null;
        }
      }
    } catch (e) {
      console.warn("Failed to load cache:", e);
    }
    if (!cachedDataRef.current) {
      setHasLoadedFromCache(false);
    }
  }, [cacheKey, currentPage, pageSize]);

  // Fetch credit allocation history with pagination
  const fetchHistory = useCallback(
    async (page = currentPage, size = pageSize, forceRefresh = false) => {
      if (!selectedBrand) return;

      // Create unique key for this fetch request
      const fetchKey = `${selectedBrand.id}-${page}-${size}`;

      // Prevent duplicate simultaneous requests
      if (fetchingRef.current) {
        return;
      }

      // If we just fetched this exact same data, skip
      if (!forceRefresh && lastFetchKeyRef.current === fetchKey) {
        return;
      }

      // Check cache first if not forcing refresh
      const cacheKeyForPage = `page-${page}-size-${size}`;
      if (
        !forceRefresh &&
        cacheKey &&
        cachedDataRef.current &&
        cachedDataRef.current[cacheKeyForPage]
      ) {
        const cached = cachedDataRef.current[cacheKeyForPage];
        setHistoryData(cached.results || []);
        setTotalCount(cached.count || 0);
        setLoading(false);
        lastFetchKeyRef.current = fetchKey;
        return;
      }

      // Mark as fetching
      fetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      try {
        setLoading(true);
        const response = await getCreditAllocationHistory({
          brand: selectedBrand.id,
          page: page,
          page_size: size,
          date: dateFilter,
        });

        // Handle different response formats
        let pageResults = [];
        let pageCount = 0;

        if (response?.results) {
          pageResults = response.results;
          pageCount = response.count || 0;
        } else if (response?.data?.results) {
          pageResults = response.data.results;
          pageCount = response.data.count || 0;
        } else if (Array.isArray(response)) {
          pageResults = response;
          pageCount = response.length;
        } else if (response?.data && Array.isArray(response.data)) {
          pageResults = response.data;
          pageCount = response.data.length;
        }

        setHistoryData(pageResults);
        setTotalCount(pageCount || pageResults.length);

        // Cache the results
        if (cacheKey) {
          try {
            const cacheData = cachedDataRef.current || {};
            cacheData[`page-${page}-size-${size}`] = {
              results: pageResults,
              count: pageCount || pageResults.length,
              timestamp: Date.now(),
            };
            cachedDataRef.current = cacheData;
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ data: cacheData, timestamp: Date.now() })
            );
          } catch (e) {
            console.warn("Failed to save cache:", e);
          }
        }

        // Update filtered count after data is set
        setTimeout(() => {
          if (gridApiRef.current) {
            let filtered = 0;
            gridApiRef.current.forEachNodeAfterFilter(() => {
              filtered++;
            });
            setFilteredCount(filtered);
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching credit allocation history:", error);
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unknown error";
        message.error(`Failed to load credit allocation history: ${errorMsg}`);
        setHistoryData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [selectedBrand, dateFilter, currentPage, pageSize, cacheKey]
  );

  // Keep ref updated with latest fetchHistory
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  // Handle pagination changes
  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      setCurrentPage(newPage);
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    },
    [pageSize]
  );

  // Fetch credit allocation history when selected brand or pagination changes
  // Only fetch if we haven't loaded from cache or if brand/page changed
  useEffect(() => {
    if (!selectedBrand) {
      setHistoryData([]);
      setTotalCount(0);
      setHasLoadedFromCache(false);
      lastFetchKeyRef.current = null;
      return;
    }

    // If we just loaded from cache, don't fetch again
    if (hasLoadedFromCache) {
      setHasLoadedFromCache(false);
      return;
    }

    // Small delay to batch multiple rapid changes
    const timeoutId = setTimeout(() => {
      if (!fetchingRef.current && fetchHistoryRef.current) {
        fetchHistoryRef.current(currentPage, pageSize, false);
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedBrand, currentPage, pageSize]);

  // Handle rollback to a previous version
  const handleRollback = (historyId, recordDate) => {
    Modal.confirm({
      title: "Confirm Rollback",
      content: `Are you sure you want to roll back this credit allocation to the version from ${moment(
        recordDate
      ).format("YYYY-MM-DD HH:mm:ss")}?`,
      okText: "Yes, Roll Back",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setRollbackLoading(true);
          const result = await rollbackCreditAllocation(historyId);
          // Handle different response structures
          const successMsg =
            result?.message ||
            result?.data?.message ||
            "Credit allocation rolled back successfully";
          message.success(successMsg);
          // Refresh the history data
          fetchHistory(currentPage, pageSize, true);
        } catch (err) {
          // Handle different error response structures
          const errorMsg =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.response?.data?.message ||
            err.message ||
            "Unknown error";
          message.error("Failed to roll back credit allocation: " + errorMsg);
          console.error("Rollback error:", err);
        } finally {
          setRollbackLoading(false);
        }
      },
    });
  };

  // Format the history type for display
  const formatHistoryType = (type) => {
    switch (type) {
      case "+":
        return <Tag color="green">Created</Tag>;
      case "-":
        return <Tag color="red">Deleted</Tag>;
      case "~":
        return <Tag color="blue">Updated</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  // Format field changes for display
  const formatChanges = (fieldChanges) => {
    if (
      !fieldChanges ||
      (typeof fieldChanges === "object" &&
        Object.keys(fieldChanges).length === 0)
    ) {
      return <Tag color="gray">No changes</Tag>;
    }

    if (fieldChanges.created) {
      return <Tag color="green">Credit allocation created</Tag>;
    }

    if (fieldChanges.deleted) {
      return <Tag color="red">Credit allocation deleted</Tag>;
    }

    // Handle explicit rollback with no detected changes
    if (
      fieldChanges.explicit_rollback ||
      (fieldChanges.rollback && Object.keys(fieldChanges).length <= 2)
    ) {
      return <Tag color="blue">Rolled back credit allocation</Tag>;
    }

    // Filter out metadata fields
    const displayableChanges = Object.entries(fieldChanges).filter(
      ([field]) =>
        !["rollback", "rollback_source", "explicit_rollback"].includes(field)
    );

    // If no displayable changes after filtering metadata
    if (displayableChanges.length === 0) {
      return fieldChanges.rollback ? (
        <Tag color="blue">Rolled back credit allocation</Tag>
      ) : (
        <Tag color="gray">No changes</Tag>
      );
    }

    return (
      <Space direction="vertical">
        {displayableChanges.map(([field, { old, new: newValue }]) => {
          // Format dates
          if (field.includes("date")) {
            old = old ? moment(old).format("YYYY-MM-DD") : "None";
            newValue = newValue
              ? moment(newValue).format("YYYY-MM-DD")
              : "None";
          }

          // Format monetary values using global display currency
          if (["amount"].includes(field)) {
            old = typeof old === "number" ? formatInDisplayCurrency(old) : old;
            newValue =
              typeof newValue === "number"
                ? formatInDisplayCurrency(newValue)
                : newValue;
          }

          return (
            <Tag key={field} color="blue">
              <Tooltip title={`Changed from ${old} to ${newValue}`}>
                {field.replace(/_/g, " ")}: {String(old).substring(0, 10)}
                {old && String(old).length > 10 ? "..." : ""} →{" "}
                {String(newValue).substring(0, 10)}
                {newValue && String(newValue).length > 10 ? "..." : ""}
              </Tooltip>
            </Tag>
          );
        })}
        {fieldChanges.rollback && <Tag color="blue">Rollback operation</Tag>}
      </Space>
    );
  };

  // AG Grid column definitions
  const columnDefs = [
    {
      headerName: "Date",
      field: "history_date",
      width: 150,
      sortable: true,
      filter: "agDateColumnFilter",
      cellRenderer: (params) =>
        moment(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      headerName: "Credit",
      field: "credit_number",
      width: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params) => params.value || "N/A",
    },
    {
      headerName: "Invoice",
      field: "invoice_number",
      width: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params) => params.value || "N/A",
    },
    {
      headerName: "Amount",
      field: "amount",
      width: 120,
      sortable: true,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => {
        if (!params.value) return "N/A";
        const currency = params.data?.currency || "$";
        return formatCurrencyWithConversion(params.value, currency);
      },
    },
    {
      headerName: "Allocation Date",
      field: "allocation_date",
      width: 130,
      sortable: true,
      filter: "agDateColumnFilter",
      cellRenderer: (params) =>
        params.value ? moment(params.value).format("YYYY-MM-DD") : "N/A",
    },
    {
      headerName: "Action",
      field: "history_type",
      width: 100,
      sortable: true,
      filter: true,
      cellRenderer: (params) => formatHistoryType(params.value),
    },
    {
      headerName: "User",
      field: "history_username",
      width: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params) => params.value || "System",
    },
    {
      headerName: "Changes",
      field: "field_changes",
      width: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params) => formatChanges(params.value),
      autoHeight: true,
    },
    {
      headerName: "Change Reason",
      field: "history_change_reason",
      width: 150,
      sortable: true,
      filter: true,
      cellRenderer: (params) => params.value || "N/A",
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const isDeletion = params.data.history_type === "-";

        return (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            size="small"
            onClick={() =>
              handleRollback(params.data.history_id, params.data.history_date)
            }
            loading={rollbackLoading}
            disabled={isDeletion}
          >
            Rollback
          </Button>
        );
      },
    },
  ];

  // Update filtered count when filters change
  const updateFilteredCount = useCallback(() => {
    if (gridApiRef.current) {
      let count = 0;
      gridApiRef.current.forEachNodeAfterFilter(() => {
        count++;
      });
      setFilteredCount(count);
    }
  }, []);

  return (
    <Card
      title="Credit Allocation History"
      style={{ height: "100%" }}
      extra={
        <Space size="large">
          <Statistic
            title="Total Allocations"
            value={totalCount}
            valueStyle={{ fontSize: 18, fontWeight: "bold" }}
          />
          {filteredCount !== totalCount && (
            <Statistic
              title="Filtered"
              value={filteredCount}
              valueStyle={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#1890ff",
              }}
            />
          )}
        </Space>
      }
    >
      <div
        className="ag-theme-alpine"
        style={{
          height: "70vh",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <AgGridWithColumnState
          key={`credit-allocation-history-${currencyRefreshKey}`}
          tableId="credit-allocation-history"
          gridApiRef={gridApiRef}
          columnDefs={columnDefs}
          rowData={historyData}
          loading={loading}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100, 200, 500]}
          paginationPosition="top"
          onPaginationChanged={(params) => {
            if (params.newPage !== undefined) {
              const newPage = params.api.paginationGetCurrentPage() + 1;
              const newPageSize = params.api.paginationGetPageSize();
              if (newPage !== currentPage || newPageSize !== pageSize) {
                handlePaginationChange(newPage, newPageSize);
              }
            }
          }}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
          }}
          animateRows={true}
          rowSelection="single"
          suppressRowClickSelection={true}
          getRowId={(params) => params.data.history_id}
          onGridReady={(params) => {
            gridApiRef.current = params.api;
            params.api.sizeColumnsToFit();
            updateFilteredCount();
          }}
          onFilterChanged={updateFilteredCount}
          onSortChanged={updateFilteredCount}
        />
      </div>
    </Card>
  );
};

export default CreditAllocationHistory;
